import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { parametersApi } from '../../services/api';

function ParametersTab() {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    weight: 0.1,
    data_type: 'integer',
    min_value: 1,
    max_value: 10,
    is_required: false,
    options: [],
    display_order: 0,
  });

  useEffect(() => {
    fetchParameters();
  }, []);

  const fetchParameters = async () => {
    try {
      const response = await parametersApi.getAll();
      setParameters(response.data);
    } catch (error) {
      console.error('Error fetching parameters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParameter = () => {
    setSelectedParameter(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      weight: 0.1,
      data_type: 'integer',
      min_value: 1,
      max_value: 10,
      is_required: false,
      options: [],
      display_order: parameters.length,
    });
    setOpenDialog(true);
  };

  const handleEditParameter = (parameter) => {
    setSelectedParameter(parameter);
    setFormData({
      name: parameter.name,
      display_name: parameter.display_name || '',
      description: parameter.description || '',
      weight: parameter.weight,
      data_type: parameter.data_type,
      min_value: parameter.min_value || 1,
      max_value: parameter.max_value || 10,
      is_required: parameter.is_required,
      options: parameter.options || [],
      display_order: parameter.display_order,
    });
    setOpenDialog(true);
  };

  const handleDeleteParameter = async (parameterId) => {
    if (window.confirm('Are you sure you want to delete this parameter?')) {
      try {
        await parametersApi.delete(parameterId);
        setParameters(parameters.filter(p => p.id !== parameterId));
        setSnackbar({ open: true, message: 'Parameter deleted successfully', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to delete parameter', severity: 'error' });
      }
    }
  };

  const handleSaveParameter = async () => {
    try {
      if (!formData.name.trim()) {
        setSnackbar({ open: true, message: 'Parameter name is required', severity: 'error' });
        return;
      }

      const parameterData = {
        ...formData,
        name: formData.name.toLowerCase().replace(/\s+/g, '_'),
      };

      if (selectedParameter) {
        const response = await parametersApi.update(selectedParameter.id, parameterData);
        setParameters(parameters.map(p => p.id === selectedParameter.id ? response.data : p));
        setSnackbar({ open: true, message: 'Parameter updated successfully', severity: 'success' });
      } else {
        const response = await parametersApi.create(parameterData);
        setParameters([...parameters, response.data]);
        setSnackbar({ open: true, message: 'Parameter created successfully', severity: 'success' });
      }
      setOpenDialog(false);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save parameter', severity: 'error' });
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const totalWeight = parameters.reduce((sum, param) => sum + param.weight, 0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Prioritization Parameters
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddParameter}
        >
          Add Parameter
        </Button>
      </Box>

      {totalWeight !== 1.0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Total weight is {totalWeight.toFixed(2)}. For optimal results, weights should sum to 1.0.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Weight</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameters.map((parameter) => (
              <TableRow key={parameter.id}>
                <TableCell>{parameter.name}</TableCell>
                <TableCell>{parameter.display_name}</TableCell>
                <TableCell>
                  <Chip 
                    label={parameter.data_type} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {(parameter.weight * 100).toFixed(1)}%
                </TableCell>
                <TableCell>
                  {parameter.is_required ? 'Yes' : 'No'}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditParameter(parameter)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteParameter(parameter.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedParameter ? 'Edit Parameter' : 'Add New Parameter'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Parameter Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            
            <TextField
              label="Display Name"
              value={formData.display_name}
              onChange={(e) => handleFormChange('display_name', e.target.value)}
              fullWidth
              margin="normal"
              helperText="Leave empty to use parameter name"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={2}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Data Type</InputLabel>
              <Select
                value={formData.data_type}
                onChange={(e) => handleFormChange('data_type', e.target.value)}
                label="Data Type"
              >
                <MenuItem value="integer">Integer</MenuItem>
                <MenuItem value="float">Float</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
                <MenuItem value="string">String</MenuItem>
                <MenuItem value="select">Select</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography gutterBottom>Weight: {(formData.weight * 100).toFixed(1)}%</Typography>
              <Slider
                value={formData.weight}
                onChange={(e, value) => handleFormChange('weight', value)}
                min={0}
                max={1}
                step={0.01}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${(value * 100).toFixed(1)}%`}
              />
            </Box>

            {(formData.data_type === 'integer' || formData.data_type === 'float') && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Min Value"
                  type="number"
                  value={formData.min_value}
                  onChange={(e) => handleFormChange('min_value', parseFloat(e.target.value))}
                  margin="normal"
                />
                <TextField
                  label="Max Value"
                  type="number"
                  value={formData.max_value}
                  onChange={(e) => handleFormChange('max_value', parseFloat(e.target.value))}
                  margin="normal"
                />
              </Box>
            )}

            {formData.data_type === 'select' && (
              <TextField
                label="Options (comma-separated)"
                value={formData.options.join(', ')}
                onChange={(e) => handleFormChange('options', e.target.value.split(',').map(s => s.trim()))}
                fullWidth
                margin="normal"
                helperText="Enter options separated by commas"
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_required}
                  onChange={(e) => handleFormChange('is_required', e.target.checked)}
                />
              }
              label="Required"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveParameter} variant="contained">
            {selectedParameter ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ParametersTab;